import React, { useState, useRef, useEffect } from 'react';
import { VisuallyHidden } from './VisuallyHidden';
import { getCampaign } from '../api/api'; // Adjust the import path as needed

const RefreshButton = ({
  onRefresh,
  label = 'Refresh',
  className = '',
  disabled = false,
  setAlert,
  throttle = 1000,
  showTemplateDropdown = false,
  campaign = null,
  currentTemplate = null
}) => {
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const lastRef = useRef(0);
  const statusRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch templates when dropdown is opened
  useEffect(() => {
    if (isDropdownOpen && showTemplateDropdown && templates.length === 0) {
      fetchTemplates();
    }
  }, [isDropdownOpen, showTemplateDropdown]);

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      if (!campaign) {
        console.warn('No campaign provided to fetch templates');
        setTemplates([]);
        return;
      }

      const response = await getCampaign(campaign);
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error fetching templates for campaign:', error);
      if (setAlert) {
        setAlert({
          variant: 'error',
          heading: 'Error',
          text: `Failed to load templates for campaign "${campaign}". Please try again.`
        });
      }
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleClick = async (template = null) => {
    if (loading || disabled) return;
    const now = Date.now();
    if (now - lastRef.current < throttle) return;
    lastRef.current = now;

    setLoading(true);
    setIsDropdownOpen(false);
    if (statusRef.current) statusRef.current.textContent = 'Refreshing...';

    // Use currentTemplate if no template is provided (main button click)
    const templateToUse = template || currentTemplate || '';

    try {
      await onRefresh(templateToUse);
      if (statusRef.current) statusRef.current.textContent = 'Refresh complete';

      if (setAlert && templateToUse) {
        setAlert({
          variant: 'success',
          heading: 'Success',
          text: `Summary refreshed using template: ${typeof templateToUse === 'object' ? templateToUse.templateName || templateToUse.name || templateToUse.title : templateToUse}`
        });
      }
    } catch (err) {
      console.error('Refresh failed', err);
      if (setAlert) {
        setAlert({
          variant: 'error',
          heading: 'Error',
          text: 'Unable to refresh. Please try again.'
        });
      }
      if (statusRef.current) statusRef.current.textContent = 'Refresh failed';
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (statusRef.current) statusRef.current.textContent = '';
      }, 1000);
    }
  };

  const handleTemplateSelect = (template) => {
    handleClick(template);
  };

  if (!showTemplateDropdown) {
    // Original simple refresh button
    return (
      <button
        onClick={() => handleClick()}
        disabled={loading || disabled}
        className={`btn-outline disabled:opacity-50 ${className}`}
        aria-busy={loading}
      >
        {loading ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        )}
        {loading ? 'Refreshing...' : label}
        <VisuallyHidden>
          <span ref={statusRef} aria-live="polite" />
        </VisuallyHidden>
      </button>
    );
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div className="flex">
        {/* Main refresh button */}
        <button
          onClick={() => handleClick()}
          disabled={loading || disabled}
          className={`btn-outline disabled:opacity-50 rounded-r-none border-r-0 ${className}`}
          aria-busy={loading}
        >
          {loading ? (
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          )}
          {loading ? 'Refreshing...' : label}
        </button>

        {/* Dropdown toggle */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={loading || disabled}
          className={`btn-outline disabled:opacity-50 rounded-l-none px-2 ${className}`}
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
        >
          <svg
            className={`h-3 w-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
              <strong>Select Template:</strong>
            </div>
            
            {isLoadingTemplates ? (
              <div className="px-4 py-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <svg
                    className="animate-spin h-3 w-3 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading templates...
                </div>
              </div>
            ) : templates.length > 0 ? (
              templates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateSelect(template)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                  disabled={loading}
                >
                  {typeof template === 'object' ? template.templateName || template.name || template.title || template.label : template}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No templates available
              </div>
            )}
          </div>
        </div>
      )}

      <VisuallyHidden>
        <span ref={statusRef} aria-live="polite" />
      </VisuallyHidden>
    </div>
  );
};

export default RefreshButton;